/**
 * Auto Token Refresh Hook
 * 
 * Automatically refreshes JWT tokens before they expire
 * Prevents Socket.IO disconnections and API 401 errors
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { isPublicRoute } from '@/lib/publicRoutes';

interface TokenPayload {
  userId: string;
  email: string;
  name: string;
  loginTime: number;
  exp: number; // Expiration timestamp (seconds)
  iat: number; // Issued at timestamp (seconds)
}

/**
 * Decode JWT token without verification (client-side only)
 */
function decodeToken(token: string): TokenPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Hook to automatically refresh authentication tokens
 * 
 * @param refreshBeforeMinutes - Minutes before expiration to trigger refresh (default: 60)
 */
export function useAutoTokenRefresh(refreshBeforeMinutes: number = 60) {
  const refreshTimerRef = useRef<number | null>(null);
  const isRefreshingRef = useRef(false);
  const location = useLocation();

  const scheduleTokenRefresh = (token: string) => {
    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) {
      console.warn('⚠️ Cannot schedule refresh: Invalid token');
      return;
    }

    // Calculate time until refresh (in milliseconds)
    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const refreshAt = expiresAt - (refreshBeforeMinutes * 60 * 1000);
    const timeUntilRefresh = refreshAt - now;

    if (timeUntilRefresh <= 0) {
      // Token already needs refresh
      console.log('🔄 Token needs immediate refresh');
      refreshToken();
      return;
    }

    console.log(`⏰ Token will be refreshed in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);

    // Schedule refresh
    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, timeUntilRefresh);
  };

  const refreshToken = async () => {
    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      console.log('🔄 Token refresh already in progress');
      return;
    }

    isRefreshingRef.current = true;

    try {
      console.log('🔄 Refreshing authentication token...');
      const response = await api.refreshToken();

      if (response.success && response.token) {
        // Update localStorage
        localStorage.setItem('auth_token', response.token);
        if (response.user) {
          localStorage.setItem('user_data', JSON.stringify(response.user));
        }

        console.log('✅ Token refreshed successfully');

        // Dispatch event to notify other components (e.g., Socket.IO)
        window.dispatchEvent(new CustomEvent('auth:token-refreshed', {
          detail: { token: response.token }
        }));

        // Schedule next refresh
        scheduleTokenRefresh(response.token);
      } else {
        console.error('❌ Token refresh failed:', response.message);
      }
    } catch (error: any) {
      console.error('❌ Token refresh error:', error);

      // Distinguish server-confirmed dead session vs. transient network/5xx failure.
      // Only nuke local auth state + dispatch expired event for definitive server failures.
      // Everything else stays quiet and re-tries later — a network blip should never log
      // the user out.
      const message = String(error?.message || '');
      const isDefinitiveFailure =
        message.includes('deactivated') ||
        message.includes('no longer exists') ||
        message.includes('Invalid token') ||
        message.includes('User not found') ||
        message.includes('Invalid token payload');

      if (isDefinitiveFailure) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        sessionStorage.removeItem('user_profile');
        window.dispatchEvent(new CustomEvent('auth:token-expired'));
      } else {
        // Transient — try again in 60s. Existing timer already cleared at start of
        // scheduleTokenRefresh, so this replaces it cleanly.
        console.warn('⚠️ Transient refresh failure, retrying in 60s');
        refreshTimerRef.current = window.setTimeout(() => {
          const currentToken = localStorage.getItem('auth_token');
          if (currentToken) refreshToken();
        }, 60000);
      }
    } finally {
      isRefreshingRef.current = false;
    }
  };

  useEffect(() => {
    // Skip auto-refresh during admin impersonation (token is short-lived and non-refreshable)
    if (sessionStorage.getItem('impersonation_active')) {
      console.log('ℹ️ Impersonation active, skipping auto-refresh');
      return;
    }

    // Skip proactive refresh entirely on public routes. A QR-scanning visitor viewing a
    // public card, marketing page, or blog article doesn't need their token maintained,
    // and a failed refresh here would take down a page that never needed auth.
    if (isPublicRoute()) {
      console.log('ℹ️ On public route, skipping proactive token refresh');
      return;
    }

    // Get current token
    const token = localStorage.getItem('auth_token');

    if (!token) {
      console.log('ℹ️ No token found, skipping auto-refresh');
      return;
    }

    // Schedule initial refresh
    scheduleTokenRefresh(token);

    // Listen for manual token updates (e.g., from login)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue) {
        console.log('🔄 Token updated externally, rescheduling refresh');
        scheduleTokenRefresh(e.newValue);
      }
    };

    const handleTokenRefreshed = (event: CustomEvent) => {
      const { token } = event.detail;
      if (token) {
        scheduleTokenRefresh(token);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener);

    // Cleanup
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener);
    };
  }, [refreshBeforeMinutes, location.pathname]);

  return {
    refreshToken,
  };
}
